/**
 * A REACTION THAT MIXES DRAWN MOLECULES WITH WRITTEN ONES.
 *
 * ── WHY THIS IS A LAYER AND NOT A CHANGE TO `renderChemFormula` ───────────────────────────────
 * KaTeX renders one string to one block of HTML. A structure is an SVG from a different library
 * entirely, so a reaction holding both cannot be one call to either of them — it has to be
 * composed. This walks the tokens, hands every run of written chemistry to KaTeX as one piece,
 * draws each structure, and lays the results side by side.
 *
 * ── THE FAST PATH IS BYTE-IDENTICAL, ON PURPOSE ───────────────────────────────────────────────
 * A reaction with no structures in it — which is most of chemistry, and all content written
 * before today — returns `renderChemFormula` untouched. Not approximately: the same call, the
 * same string. Composition only begins when there is something to compose, so nothing that
 * worked yesterday renders through new code today.
 *
 * ── EXCEPT THAT THE LEWIS GRAMMAR IS BROUGHT UP TO DATE FIRST ─────────────────────────────────
 * The one thing re-spelled on the way in. The electrons' notation is STORED, so a correction to
 * how they are drawn never reaches a reaction already written — see `upgradeLewis`, which is a
 * no-op on everything written in the current spelling.
 *
 * ── RUNS, NOT TOKENS ──────────────────────────────────────────────────────────────────────────
 * Consecutive written tokens go to KaTeX together rather than one at a time. mhchem decides
 * spacing and line-breaking from what surrounds a species, so rendering `2H2O`, `+` and `O2`
 * separately spaces them like three unrelated fragments instead of one side of an equation.
 */

import { renderChemFormula } from './chem-formula-extension';
import { coSubstrateMarkup, hasCoSubstrate } from './reaction-cosubstrate';
import { upgradeLewis } from './reaction-lewis';
import { serializeToken, tokenize, type Token } from './reaction-tokens';
import { structurePicture } from './structure-picture';

export type ComposedReaction = {
  html: string;
  error: string | null;
  /** True when a structure is present, so a caller knows the chemistry library is needed. */
  hasStructure: boolean;
  /** True when something could not be drawn yet because the library is still loading. */
  pending: boolean;
};

/** Does this source need the chemistry library at all? Cheap, and answers before any work. */
export function needsChemistry(source: string): boolean {
  return source.includes('[smiles:');
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** One drawn molecule, boxed so it sits on the line with the written chemistry beside it. */
function structureHtml(token: Token, index: number): { html: string; pending: boolean; error: string | null } {
  /* Drawn in the highlight colour when it carries one, rather than in CPK. See
     `tintStructureSvg` for why a highlighted molecule gives its element hues up. */
  const colour = token.marks?.find(m => m.kind === 'color')?.value;
  /* Everything about WHAT is drawn lives in one place, shared with the node view — see
     `structure-picture`, and the two paths that drifted apart before it existed. */
  const made = structurePicture({
    smiles: token.text,
    name: token.structure?.name ?? '',
    arrows: token.structure?.arrows,
    annotations: token.structure?.annotations,
    id: `s${index}`,
    ...(colour ? { colour } : {}),
  });
  if (made.pending) {
    /* The name, until the drawing arrives. A teacher sees what the molecule IS rather than a
       gap, and the swap when the library lands is the only thing that moves. */
    return {
      html: `<span class="reaction-structure is-loading" data-smiles="${escapeHtml(token.text)}">${escapeHtml(made.label)}</span>`,
      pending: true,
      error: null,
    };
  }
  if (made.error) {
    /* A structure that will not parse shows its SMILES, the same way an unrenderable mhchem
       token shows its own source. Losing sight of what you typed is worse than seeing it raw. */
    return {
      html: `<span class="reaction-structure is-error">${escapeHtml(token.text)}</span>`,
      pending: false,
      error: made.error,
    };
  }
  const cls = made.hasCaptions ? 'reaction-structure has-captions' : 'reaction-structure';
  return {
    html: `<span class="${cls}" role="img" aria-label="${escapeHtml(made.label)}">${made.html}</span>`,
    pending: false,
    error: null,
  };
}

/**
 * The co-substrate arc, with its two species set at the arc's own ends.
 *
 * Each label is rendered as its own tiny piece of mhchem rather than as plain text, so `CO2`
 * gets its subscript and `-H+` its charge — they are chemistry, and the only reason they are
 * not inside the main run is that there is no mhchem that would put them here.
 */
function coSubstrateHtml(token: Token): { html: string; error: string | null } {
  const c = token.cosub!;
  let error: string | null = null;
  const side = (text: string, cls: string) => {
    if (!text) return '';
    const { html, error: err } = renderChemFormula(text);
    if (err) error ??= err;
    return `<span class="${cls}">${html}</span>`;
  };
  const into = side(c.into, 'reaction-cosub-in');
  const out = side(c.out, 'reaction-cosub-out');
  /* The arrow the arc is drawn ON is not drawn twice: the arc's own shaft IS that arrow. A
     teacher who picks a different arrow gets it in the source and, for now, the same shaft —
     see the module, which draws one shape. */
  /*
   * THE SLOT AROUND IT, which is what puts the shaft on the reaction's own line.
   *
   * The composed render is a flex row aligned on BASELINES, and the arc has no text in it to
   * have one — so it was centred on the flex line instead. A flex line's height is set by its
   * tallest item, and with either species named the arc IS the tallest: centring it in a line
   * its own height put the shaft in the middle while the baseline-aligned chemistry hung from
   * the top. Measured at a 26px reaction, the shaft sat 22px — most of an em — below the
   * species' own middle, every time a label was present and never when one was not. Reported
   * as the curved arrow being way off.
   *
   * An inline-block HAS a baseline, so the slot is baseline-aligned like the chemistry and the
   * arc is placed inside it with `vertical-align` — a measurement from the baseline, which is
   * the only thing that does not move when something hangs above or below. See the stylesheet,
   * which holds the two numbers.
   */
  return {
    html: `<span class="reaction-cosub-slot">`
      + `<span class="reaction-cosub">${into}${coSubstrateMarkup(2.1, c)}${out}</span>`
      + `</span>`,
    error,
  };
}

export function composeReaction(raw: string): ComposedReaction {
  const source = upgradeLewis(raw);
  if (!needsChemistry(source) && !hasCoSubstrate(source)) {
    const { html, error } = renderChemFormula(source);
    return { html, error, hasStructure: false, pending: false };
  }

  const tokens = tokenize(source);
  const pieces: string[] = [];
  let run: Token[] = [];
  let error: string | null = null;
  let pending = false;

  const flushRun = () => {
    if (!run.length) return;
    const written = run.map(serializeToken).join(' ');
    const { html, error: err } = renderChemFormula(written);
    if (err) error ??= err;
    pieces.push(`<span class="reaction-written">${html}</span>`);
    run = [];
  };

  tokens.forEach((token, i) => {
    if (token.cosub) {
      flushRun();
      const arc = coSubstrateHtml(token);
      if (arc.error) error ??= arc.error;
      pieces.push(arc.html);
      return;
    }
    if (!token.structure) { run.push(token); return; }
    flushRun();
    const drawn = structureHtml(token, i);
    if (drawn.error) error ??= drawn.error;
    if (drawn.pending) pending = true;
    pieces.push(drawn.html);
  });
  flushRun();

  return {
    html: `<span class="reaction-composed">${pieces.join('')}</span>`,
    error,
    /* An arc needs no chemistry library — it is our own drawing — so a reaction that has one
       and no structure must not report that it does, or the caller loads OpenChemLib to draw
       nothing. */
    hasStructure: needsChemistry(source),
    pending,
  };
}
